import {
  useCreateOrUpdateMultipleSampleInExhibition,
  useSyncItinerantExhibitionGroup,
} from "@/api/related-fields";
import { LinkableType } from "@/components/LinksManagement";
import { FormLayoutElement } from "@/config/forms/general";
import { MultipleSampleInExhibition } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";
import {
  ItinerantExhibitionPanel,
  ItinerantStopDraft,
  itinerantStopFromExhibition,
} from "./ItinerantExhibitionPanel";
import { ItinerantExhibitionPreview } from "./ItinerantExhibitionPreview";
import { ItinerantDrawerConfig } from "./itinerant-drawer-types";
import { useItinerantDrawerChrome } from "./useItinerantDrawerChrome";
import { useItinerantStopSelection } from "./useItinerantStopSelection";

const MultiplesampleInExhibitionDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
  itinerantConfig,
}: {
  fields: FormLayoutElement[] | undefined;
  data: MultipleSampleInExhibition;
  queryId: string;
  type: LinkableType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  itinerantConfig?: ItinerantDrawerConfig;
}) => {
  const schema = z.object({
    multiplesampleDansExpositionsDescriptionNumeroCatalogue: z
      .string()
      .optional(),
    multiplesampleDansExpositionsNotes: z.string().optional(),
    exhibition: z.object({
      expositionsDescriptionNumeroExpo: z.string(),
      expositionsDescriptionResume: z.string(),
    }),
  });

  const { mutate: createOrUpdateMultipleSampleInExhibition } =
    useCreateOrUpdateMultipleSampleInExhibition(queryId, type);

  const { mutate: syncGroup, isPending: isSyncing } =
    useSyncItinerantExhibitionGroup(
      itinerantConfig?.parentId ?? "",
      itinerantConfig?.parentType ?? "multiplesample",
      itinerantConfig?.complexRelationKey,
    );

  const [stops, setStops] = useState<ItinerantStopDraft[]>([]);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      exhibition: data.exhibition,
      multiplesampleDansExpositionsDescriptionNumeroCatalogue:
        data.multiplesampleDansExpositionsDescriptionNumeroCatalogue,
      multiplesampleDansExpositionsNotes:
        data.multiplesampleDansExpositionsNotes,
    },
  });

  const { selectedStopId, selectStop, flushStopsForSubmit } =
    useItinerantStopSelection(itinerantConfig, stops, setStops, form, {
      catalogNumber: "multiplesampleDansExpositionsDescriptionNumeroCatalogue",
    });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    if (itinerantConfig) {
      const effectiveStops = flushStopsForSubmit();
      const stopsToSync =
        effectiveStops.length > 0
          ? effectiveStops
          : [
              itinerantStopFromExhibition(itinerantConfig.seedExhibition, undefined, {
                catalogNumber:
                  formData.multiplesampleDansExpositionsDescriptionNumeroCatalogue ??
                  "",
              }),
            ];
      syncGroup({
        groupId: itinerantConfig.groupId ?? undefined,
        parentType: itinerantConfig.parentType,
        parentId: itinerantConfig.parentId,
        stops: stopsToSync.map((s, index) => ({
          linkId: s.linkId,
          exhibitionId: s.exhibition.id,
          catalogNumber: s.catalogNumber,
          exemplaireExpose: s.exemplaireExpose,
          note: s.note,
          order: index,
        })),
      });
      return;
    }

    createOrUpdateMultipleSampleInExhibition({
      id: data.id ?? "",
      multiplesampleId: data.multiplesample.id,
      exhibitionId: data.exhibition.id,
      multiplesampleDansExpositionsDescriptionNumeroCatalogue:
        formData.multiplesampleDansExpositionsDescriptionNumeroCatalogue,
      multiplesampleDansExpositionsNotes:
        formData.multiplesampleDansExpositionsNotes,
    });
  };

  const sheetOpen = open ?? false;
  const { showItinerantSidebar, isItinerantContext, fieldsWithHint } =
    useItinerantDrawerChrome(itinerantConfig, fields);

  const catalogWatch = form.watch(
    "multiplesampleDansExpositionsDescriptionNumeroCatalogue",
  );
  const previewStops = useMemo(
    () =>
      stops.map((stop) =>
        stop.id === selectedStopId
          ? { ...stop, catalogNumber: catalogWatch ?? stop.catalogNumber }
          : stop,
      ),
    [stops, selectedStopId, catalogWatch],
  );

  return (
    <LinksDrawerLayout
      form={form}
      fields={fieldsWithHint}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={{
        attachments: data.multiplesample.attachments ?? [],
        type: "multiplesample",
        alt:
          data.multiplesample
            .exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
      }}
      open={open}
      onOpenChange={onOpenChange}
      submitDisabled={isSyncing}
      formFooter={
        isItinerantContext ? (
          <ItinerantExhibitionPreview stops={previewStops} />
        ) : undefined
      }
      sidebar={
        showItinerantSidebar && itinerantConfig ? (
          <ItinerantExhibitionPanel
            open={sheetOpen}
            seedExhibition={itinerantConfig.seedExhibition}
            initialStops={itinerantConfig.initialStops}
            groupId={itinerantConfig.groupId}
            parentType={itinerantConfig.parentType}
            parentId={itinerantConfig.parentId}
            stops={stops}
            onStopsChange={setStops}
            inlineFields={false}
            selectedStopId={selectedStopId}
            onSelectStop={selectStop}
          />
        ) : undefined
      }
    />
  );
};

export default MultiplesampleInExhibitionDrawer;
